import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import MarkdownView from "@/components/MarkdownView";
import { toast } from "sonner";
import {
  ArrowLeft, FileDown, Save, Trash2, Loader2, ClipboardList, FileText, Sparkles,
  Eye, Pencil, Columns2, Copy, Plus, X, ChevronDown, Receipt, StopCircle, FolderKanban,
  Calculator, FolderArchive, Download, Trash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { STATUS_COLUMNS, fmtEUR } from "@/pages/OpportunitiesPage";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const OBJECTIVES = [
  "Générer des leads", "Vendre en ligne", "Augmenter la visibilité",
  "Automatiser un process interne", "Fidéliser les clients", "Moderniser l'image",
];
const EMPTY_FORM = {
  project_type: "", project_type_other: "", project_description: "",
  main_objectives: [], target_audience: "", budget_range: "",
  competitors: "", design_preferences: "", timeline: "", additional_notes: "",
};
const EMPTY_DEVIS = {
  lines: [], tva_rate: 8.5, discount_pct: 0, validity_days: 30,
  payment_terms: "30 % à la commande, 40 % à mi-parcours, 30 % à la livraison — paiement à 30 jours.",
  delay: "", notes: "",
};
// Consignes prêtes à l'emploi : un clic les ajoute à la zone de prompt.
const SUGGESTIONS = [
  "Détaille davantage le lot technique (API, performances, sécurité).",
  "Ajoute un lot maintenance et TMA sur 12 mois.",
  "Version courte : 4 lots maximum, l'essentiel seulement.",
  "Insiste sur le SEO et la performance des pages.",
  "Prévois une reprise des données depuis l'outil actuel.",
  "Ajoute un lot formation et accompagnement au démarrage.",
];

const nombre = (v) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : 0);

/** Totaux du devis — même formule que le rendu PDF côté serveur. */
export function calculDevis(devis) {
  const lignes = devis?.lines || [];
  const detail = lignes.map((l) => nombre(l.quantity) * nombre(l.unit_price));
  const total_ht = detail.reduce((a, b) => a + b, 0);
  const remise_pct = nombre(devis?.discount_pct);
  const remise = (total_ht * remise_pct) / 100;
  const net_ht = total_ht - remise;
  const taux_tva = devis?.tva_rate === undefined || devis?.tva_rate === null ? 8.5 : nombre(devis.tva_rate);
  const tva = (net_ht * taux_tva) / 100;
  return { detail, total_ht, remise_pct, remise, net_ht, taux_tva, tva, ttc: net_ht + tva };
}

const euros = (v) =>
  `${nombre(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function OpportunityDetailPage() {
  const { oppId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [onglet, setOnglet] = useState("decouverte");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pdfing, setPdfing] = useState(false);

  // ── Cahier des charges & devis ──
  const [cdc, setCdc] = useState("");
  const [cdcRef, setCdcRef] = useState("");
  const [promptIA, setPromptIA] = useState("");
  const [avecDevis, setAvecDevis] = useState(true);
  const [tjm, setTjm] = useState(600);
  const [devis, setDevis] = useState(EMPTY_DEVIS);
  const [vue, setVue] = useState("duo");
  const [generating, setGenerating] = useState(false);
  const [etape, setEtape] = useState("");
  const [chrono, setChrono] = useState(0);
  const [modifie, setModifie] = useState(false);
  const [savingCdc, setSavingCdc] = useState(false);
  const [exportMode, setExportMode] = useState(null);
  const [conversion, setConversion] = useState(false);
  const [chiffrage, setChiffrage] = useState(false);
  const [documents, setDocuments] = useState([]);
  const abortRef = useRef(null);
  const editeurRef = useRef(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API_URL}/api/opportunities/${oppId}`, auth)
      .then(({ data }) => {
        setOpp(data);
        if (data.discovery_form) setForm({ ...EMPTY_FORM, ...data.discovery_form });
      })
      .catch(() => {
        toast.error("Opportunité introuvable");
        navigate("/dashboard/opportunities");
      });
    axios.get(`${API_URL}/api/opportunities/${oppId}/cdc`, auth)
      .then(({ data }) => {
        setCdc(data.cdc || "");
        setPromptIA(data.prompt || "");
        setDevis({ ...EMPTY_DEVIS, ...(data.devis || {}) });
        setCdcRef(data.reference || "");
      })
      .catch(() => {});
    chargerDocuments();
    // eslint-disable-next-line
  }, [oppId]);

  const chargerDocuments = () =>
    axios.get(`${API_URL}/api/opportunities/${oppId}/documents`, auth)
      .then(({ data }) => setDocuments(data))
      .catch(() => {});

  // Chronomètre de génération : rassure sur le fait que ça travaille.
  useEffect(() => {
    if (!generating) return undefined;
    const debut = Date.now();
    const iv = setInterval(() => setChrono(Math.round((Date.now() - debut) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [generating]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleObjective = (o) =>
    setF("main_objectives", form.main_objectives.includes(o)
      ? form.main_objectives.filter((x) => x !== o)
      : [...form.main_objectives, o]);

  const saveForm = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/opportunities/${oppId}/discovery-form`, form, auth);
      setOpp((o) => ({ ...o, discovery_form: form }));
      toast.success("Fiche découverte enregistrée");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/opportunities/${oppId}`, { status }, auth);
      setOpp(data);
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error("Erreur lors du changement de statut");
    }
  };

  const telecharger = async (url, nom, drapeau) => {
    drapeau(true);
    try {
      const res = await axios.get(url, { ...auth, responseType: "blob" });
      const lien = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = lien;
      a.download = nom;
      a.click();
      URL.revokeObjectURL(lien);
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      drapeau(false);
    }
  };

  const nomSociete = (opp?.company_name || "opportunite").replace(/\s+/g, "_");

  const downloadPdf = () => telecharger(
    `${API_URL}/api/opportunities/${oppId}/discovery-form/pdf`,
    `Fiche_decouverte_${nomSociete}.pdf`, setPdfing);

  const exporterCdc = async (mode) => {
    if (!cdc.trim()) { toast.error("Rédigez ou générez le cahier des charges d'abord"); return; }
    if (modifie) await enregistrerCdc({ silencieux: true });
    setExportMode(mode);
    await telecharger(
      `${API_URL}/api/opportunities/${oppId}/cdc/pdf?mode=${mode}`,
      `${mode === "cdc_devis" ? "CDC_Devis" : "CDC"}_${nomSociete}.pdf`,
      () => {});
    setExportMode(null);
  };

  // L'affaire signée devient un projet : le CDC et le devis suivent, sans ressaisie.
  const creerProjet = async () => {
    if (opp.project_id) { navigate(`/dashboard/projects/${opp.project_id}`); return; }
    if (!window.confirm(
      `Créer le projet pour « ${opp.company_name} » ? L'opportunité passera en « Gagné » et le cahier des charges sera repris.`
    )) return;
    setConversion(true);
    try {
      if (modifie) await enregistrerCdc({ silencieux: true });
      const { data } = await axios.post(`${API_URL}/api/opportunities/${oppId}/convert-to-project`, {}, auth);
      setOpp((o) => ({ ...o, status: "won", project_id: data.project_id }));
      toast.success(data.created ? "Projet créé" : "Projet déjà existant");
      navigate(`/dashboard/projects/${data.project_id}`);
    } catch (e) {
      toast.error("La création du projet a échoué");
    } finally {
      setConversion(false);
    }
  };

  // Chiffrer sans regénérer : le cahier des charges reste tel quel.
  const chiffrerCdc = async () => {
    if (!cdc.trim()) { toast.error("Rédigez ou générez le cahier des charges d'abord"); return; }
    setChiffrage(true);
    try {
      if (modifie) await enregistrerCdc({ silencieux: true });
      const { data } = await axios.post(`${API_URL}/api/opportunities/${oppId}/devis/generate`,
        { prompt: promptIA, tjm: nombre(tjm) || 600 }, auth);
      setDevis({ ...EMPTY_DEVIS, ...data.devis });
      setModifie(false);
      toast.success("Devis chiffré à partir du cahier des charges");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chiffrage impossible");
    } finally {
      setChiffrage(false);
    }
  };

  // Le PDF est figé et conservé sur l'opportunité : trace de ce qui a été envoyé.
  const enregistrerPdf = async (mode) => {
    if (!cdc.trim()) { toast.error("Rédigez ou générez le cahier des charges d'abord"); return; }
    if (modifie) await enregistrerCdc({ silencieux: true });
    setExportMode(mode);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/opportunities/${oppId}/cdc/pdf/save?mode=${mode}`, {}, auth);
      setDocuments((liste) => [data, ...liste]);
      toast.success(`« ${data.title} » enregistré`);
    } catch (e) {
      toast.error("Enregistrement impossible");
    } finally {
      setExportMode(null);
    }
  };

  const telechargerDocument = (doc) =>
    telecharger(`${API_URL}/api/documents/${doc.id}`, doc.filename, () => {});

  const supprimerDocument = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.title} » ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${doc.id}`, auth);
      setDocuments((liste) => liste.filter((d) => d.id !== doc.id));
      toast.success("Document supprimé");
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  const removeOpp = async () => {
    if (!window.confirm(`Supprimer l'opportunité « ${opp.company_name} » ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/opportunities/${oppId}`, auth);
      toast.success("Opportunité supprimée");
      navigate("/dashboard/opportunities");
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // ── Génération IA ──────────────────────────────────────────────
  const genererCdc = async () => {
    if (generating) return;
    if (cdc.trim() && !window.confirm("Le cahier des charges actuel sera remplacé. Continuer ?")) return;
    setGenerating(true);
    setChrono(0);
    setEtape("Analyse de la fiche découverte…");
    setCdc("");
    const controleur = new AbortController();
    abortRef.current = controleur;
    let recu = "";
    try {
      const res = await fetch(`${API_URL}/api/opportunities/${oppId}/cdc/generate`, {
        method: "POST",
        signal: controleur.signal,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: promptIA, with_devis: avecDevis, tjm: nombre(tjm) || 600 }),
      });
      if (!res.ok) { toast.error("Génération impossible"); setGenerating(false); return; }
      const lecteur = res.body.getReader();
      const decodeur = new TextDecoder();
      let tampon = "";
      while (true) {
        const { done, value } = await lecteur.read();
        if (done) break;
        tampon += decodeur.decode(value, { stream: true });
        const morceaux = tampon.split("\n\n");
        tampon = morceaux.pop();
        for (const bloc of morceaux) {
          if (!bloc.startsWith("data: ")) continue;
          let evt;
          try { evt = JSON.parse(bloc.slice(6)); } catch { continue; }
          if (evt.type === "status") setEtape(evt.message);
          if (evt.type === "chunk") {
            recu += evt.text;
            setEtape("Rédaction en cours…");
            // Le bloc JSON du devis n'a pas à s'afficher dans l'éditeur.
            setCdc(recu.split("```json")[0]);
          }
          if (evt.type === "cdc") {
            setCdc(evt.cdc);
            if (evt.devis) setDevis({ ...EMPTY_DEVIS, ...evt.devis });
            setModifie(false);
            toast.success("Cahier des charges généré");
          }
          if (evt.type === "error") toast.error(evt.message);
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Connexion interrompue");
    } finally {
      setGenerating(false);
      setEtape("");
      abortRef.current = null;
    }
  };

  const arreter = () => { abortRef.current?.abort(); setGenerating(false); setEtape(""); };

  const enregistrerCdc = async ({ silencieux } = {}) => {
    setSavingCdc(true);
    try {
      await axios.put(`${API_URL}/api/opportunities/${oppId}/cdc`,
        { cdc, prompt: promptIA, devis }, auth);
      setModifie(false);
      if (!silencieux) toast.success("Cahier des charges enregistré");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingCdc(false);
    }
  };

  const majCdc = (v) => { setCdc(v); setModifie(true); };
  const majDevis = (champ, valeur) => { setDevis((d) => ({ ...d, [champ]: valeur })); setModifie(true); };
  const majLigne = (i, champ, valeur) => {
    setDevis((d) => ({ ...d, lines: d.lines.map((l, k) => (k === i ? { ...l, [champ]: valeur } : l)) }));
    setModifie(true);
  };
  const ajouterLigne = () => {
    setDevis((d) => ({
      ...d,
      lines: [...(d.lines || []), { label: "", description: "", quantity: 1, unit: "jour", unit_price: nombre(tjm) || 600 }],
    }));
    setModifie(true);
  };
  const supprimerLigne = (i) => {
    setDevis((d) => ({ ...d, lines: d.lines.filter((_, k) => k !== i) }));
    setModifie(true);
  };

  const ajouterConsigne = (texte) =>
    setPromptIA((p) => (p.trim() ? `${p.trim()}\n${texte}` : texte));

  const inserer = (extrait) => {
    const zone = editeurRef.current;
    if (!zone) { majCdc(`${cdc}\n${extrait}`); return; }
    const debut = zone.selectionStart ?? cdc.length;
    const suivant = `${cdc.slice(0, debut)}${extrait}${cdc.slice(debut)}`;
    majCdc(suivant);
    requestAnimationFrame(() => {
      zone.focus();
      zone.selectionStart = zone.selectionEnd = debut + extrait.length;
    });
  };

  if (!opp) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center py-32 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </CRMLayout>
    );
  }

  const field = (label, node) => (
    <div>
      <label className="text-xs font-semibold text-slate-700 block mb-1">{label}</label>
      {node}
    </div>
  );

  const totaux = calculDevis(devis);
  const nbLots = (cdc.match(/^##\s+/gm) || []).length;
  const nbSpecs = (cdc.match(/^\s*[-*+]\s*\[[ xX]\]/gm) || []).length;
  const ficheVide = !form.project_description && !(form.main_objectives || []).length;

  const ongletBouton = (cle, Icone, libelle, pastille) => (
    <button
      key={cle}
      onClick={() => setOnglet(cle)}
      data-testid={`opp-tab-${cle}`}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition ${
        onglet === cle
          ? "border-indigo-600 text-indigo-700"
          : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      <Icone className="w-4 h-4" /> {libelle}
      {pastille && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
    </button>
  );

  const boutonVue = (cle, Icone, libelle) => (
    <button
      onClick={() => setVue(cle)}
      title={libelle}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md transition ${
        vue === cle ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
      }`}
    >
      <Icone className="w-3.5 h-3.5" /> {libelle}
    </button>
  );

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8 max-w-6xl">
        <button
          onClick={() => navigate("/dashboard/opportunities")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au pipeline
        </button>

        {/* En-tête opportunité */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-5 flex flex-wrap items-center gap-5">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-900 truncate">{opp.company_name}</h1>
            <div className="text-sm text-slate-500 mt-0.5">
              {opp.contact_name}
              {opp.contact_phone ? ` · ${opp.contact_phone}` : ""}
              {opp.contact_email ? ` · ${opp.contact_email}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-5 ml-auto flex-wrap">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Statut</div>
              <select
                value={opp.status}
                onChange={(e) => changeStatus(e.target.value)}
                className="mt-0.5 border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-semibold bg-white"
                data-testid="opp-status-select"
              >
                {STATUS_COLUMNS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Valeur estimée</div>
              <div className="font-bold text-slate-900 tabular-nums mt-1">{fmtEUR(opp.value)}</div>
            </div>
            {opp.source && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Source</div>
                <div className="font-semibold text-slate-700 mt-1 text-sm">{opp.source}</div>
              </div>
            )}
            <Button variant={opp.project_id ? "outline" : "default"} onClick={creerProjet}
              disabled={conversion}
              className={opp.project_id ? "" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid="opp-convert-btn">
              {conversion
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <FolderKanban className="w-4 h-4 mr-1.5" />}
              {opp.project_id ? "Ouvrir le projet" : "Créer le projet"}
            </Button>
          </div>
        </div>

        {/* Onglets */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-5">
          {ongletBouton("decouverte", ClipboardList, "Fiche découverte", !!opp.discovery_form)}
          {ongletBouton("cdc", FileText, "Cahier des charges", !!cdc.trim())}
        </div>

        {onglet === "decouverte" && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h2 className="font-bold text-slate-900">Fiche découverte</h2>
          <p className="text-xs text-slate-500 mb-5">
            Le brief structuré du projet — exportable en PDF pour le client ou le dossier.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Type de projet",
              <select value={form.project_type} onChange={(e) => setF("project_type", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">—</option>
                <option value="website">Site vitrine</option>
                <option value="ecommerce">E-commerce</option>
                <option value="web_app">Application web</option>
                <option value="mobile_app">Application mobile</option>
                <option value="other">Autre (module Odoo, API…)</option>
              </select>
            )}
            {field("Échéance souhaitée",
              <select value={form.timeline} onChange={(e) => setF("timeline", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">—</option>
                <option value="immediate">Immédiat</option>
                <option value="1_3_mois">1 à 3 mois</option>
                <option value="3_6_mois">3 à 6 mois</option>
                <option value="exploring">En exploration</option>
              </select>
            )}
            {form.project_type === "other" && (
              <div className="sm:col-span-2">
                {field("Précisez le type", <Input value={form.project_type_other} onChange={(e) => setF("project_type_other", e.target.value)} />)}
              </div>
            )}
            <div className="sm:col-span-2">
              {field("Description du projet",
                <textarea value={form.project_description} onChange={(e) => setF("project_description", e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[84px]" />
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-700 block mb-1.5">Objectifs principaux</label>
              <div className="flex flex-wrap gap-2">
                {OBJECTIVES.map((o) => (
                  <button key={o} type="button" onClick={() => toggleObjective(o)}
                    className={`text-xs rounded-lg border px-3 py-1.5 font-medium transition ${
                      form.main_objectives.includes(o)
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}>
                    {o}
                  </button>
                ))}
              </div>
            </div>
            {field("Audience cible", <Input value={form.target_audience} onChange={(e) => setF("target_audience", e.target.value)} />)}
            {field("Budget envisagé",
              <select value={form.budget_range} onChange={(e) => setF("budget_range", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">—</option>
                <option>&lt; 5 000 €</option>
                <option>5 000 – 15 000 €</option>
                <option>15 000 – 40 000 €</option>
                <option>&gt; 40 000 €</option>
              </select>
            )}
            {field("Concurrents / références", <Input value={form.competitors} onChange={(e) => setF("competitors", e.target.value)} />)}
            {field("Préférences design", <Input value={form.design_preferences} onChange={(e) => setF("design_preferences", e.target.value)} />)}
            <div className="sm:col-span-2">
              {field("Notes additionnelles",
                <textarea value={form.additional_notes} onChange={(e) => setF("additional_notes", e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[64px]" />
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 mt-6">
            <Button onClick={saveForm} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-discovery-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Enregistrer la fiche
            </Button>
            <Button variant="outline" onClick={downloadPdf} disabled={pdfing} data-testid="pdf-discovery-btn">
              {pdfing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
              Exporter en PDF
            </Button>
            <Button variant="outline" onClick={() => setOnglet("cdc")}>
              <Sparkles className="w-4 h-4 mr-1.5" /> Rédiger le cahier des charges
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={removeOpp} className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
            </Button>
          </div>
        </div>
        )}

        {onglet === "cdc" && (
        <div className="space-y-5">
          {/* Zone de prompt */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <h2 className="font-bold text-slate-900">Génération assistée</h2>
                <p className="text-xs text-slate-500">
                  Claude rédige le cahier des charges à partir de la fiche découverte. Ajoutez ici vos
                  consignes : lots à détailler, contraintes, ton, périmètre à exclure.
                </p>
              </div>
            </div>

            {ficheVide && (
              <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-xs text-amber-800">
                La fiche découverte est presque vide : complétez-la d'abord, le cahier des charges n'en sera que plus juste.
              </div>
            )}

            <textarea
              value={promptIA}
              onChange={(e) => setPromptIA(e.target.value)}
              placeholder="Ex. : le client veut un tunnel de commande en 3 clics, prévois un lot reprise de données depuis WooCommerce et un lot formation."
              className="mt-4 w-full border border-slate-200 rounded-lg px-3.5 py-3 text-sm min-h-[96px] focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
              data-testid="cdc-prompt"
            />

            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" onClick={() => ajouterConsigne(s)}
                  className="text-[11px] rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600 hover:border-indigo-300 hover:text-indigo-700 transition">
                  + {s}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-4">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={avecDevis} onChange={(e) => setAvecDevis(e.target.checked)}
                  className="w-4 h-4 accent-indigo-600" />
                Chiffrer le devis en même temps
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                Taux journalier
                <input type="number" value={tjm} min={0} step={50} onChange={(e) => setTjm(e.target.value)}
                  className="w-24 border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-right tabular-nums" />
                € HT
              </label>
              <div className="flex-1" />
              {generating ? (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                    {etape} <span className="tabular-nums text-slate-400">{chrono}s</span>
                  </span>
                  <Button variant="outline" size="sm" onClick={arreter}>
                    <StopCircle className="w-4 h-4 mr-1.5" /> Arrêter
                  </Button>
                </div>
              ) : (
                <Button onClick={genererCdc} className="bg-indigo-600 hover:bg-indigo-700" data-testid="cdc-generate-btn">
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  {cdc.trim() ? "Régénérer le cahier des charges" : "Générer le cahier des charges"}
                </Button>
              )}
            </div>
          </div>

          {/* Éditeur du CDC */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
              <div className="min-w-0">
                <h2 className="font-bold text-slate-900 text-sm">Cahier des charges</h2>
                <p className="text-[11px] text-slate-500">
                  {nbLots} lot{nbLots > 1 ? "s" : ""} · {nbSpecs} spécification{nbSpecs > 1 ? "s" : ""}
                  {cdcRef ? ` · Réf. ${cdcRef}` : ""}
                  {modifie && <span className="text-amber-600 font-semibold"> · modifications non enregistrées</span>}
                </p>
              </div>
              <div className="flex-1" />
              <div className="inline-flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
                {boutonVue("edition", Pencil, "Éditer")}
                {boutonVue("duo", Columns2, "Duo")}
                {boutonVue("apercu", Eye, "Aperçu")}
              </div>
              <Button variant="outline" size="sm" onClick={() => {
                navigator.clipboard.writeText(cdc);
                toast.success("Markdown copié");
              }}>
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier
              </Button>
            </div>

            {vue !== "apercu" && (
              <div className="flex flex-wrap items-center gap-1.5 px-5 py-2 border-b border-slate-100 text-[11px]">
                <span className="text-slate-400 font-semibold uppercase tracking-wider mr-1">Insérer</span>
                <button onClick={() => inserer("\n## Nouveau lot\n")} className="rounded border border-slate-200 px-2 py-1 hover:border-indigo-300 hover:text-indigo-700">## Lot</button>
                <button onClick={() => inserer("\n### Nouvelle section\n")} className="rounded border border-slate-200 px-2 py-1 hover:border-indigo-300 hover:text-indigo-700">### Section</button>
                <button onClick={() => inserer("- [ ] Nouvelle spécification\n")} className="rounded border border-slate-200 px-2 py-1 hover:border-indigo-300 hover:text-indigo-700">- [ ] Spéc.</button>
                <button onClick={() => inserer("\n| Colonne | Colonne |\n| --- | --- |\n| Valeur | Valeur |\n")} className="rounded border border-slate-200 px-2 py-1 hover:border-indigo-300 hover:text-indigo-700">Tableau</button>
              </div>
            )}

            <div className={`grid ${vue === "duo" ? "md:grid-cols-2" : "grid-cols-1"}`}>
              {vue !== "apercu" && (
                <textarea
                  ref={editeurRef}
                  value={cdc}
                  onChange={(e) => majCdc(e.target.value)}
                  placeholder="## 1. Contexte & objectifs&#10;### Situation actuelle&#10;- [ ] Première spécification…"
                  spellCheck={false}
                  data-testid="cdc-editor"
                  className="w-full font-mono text-[12.5px] leading-[1.65] text-slate-700 p-5 min-h-[560px] resize-y focus:outline-none border-b md:border-b-0 md:border-r border-slate-100"
                />
              )}
              {vue !== "edition" && (
                <div className="p-5 min-h-[560px] overflow-x-auto bg-white" data-testid="cdc-preview">
                  {cdc.trim() ? (
                    <MarkdownView source={cdc} />
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-16">
                      <FileText className="w-8 h-8 mb-2" />
                      <p className="text-sm">Aucun cahier des charges pour l'instant.</p>
                      <p className="text-xs">Lancez la génération ou rédigez-le à la main.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Devis structuré */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
              <Receipt className="w-4 h-4 text-indigo-600" />
              <div>
                <h2 className="font-bold text-slate-900 text-sm">Devis structuré</h2>
                <p className="text-[11px] text-slate-500">Chiffrage joint au PDF « CDC + devis ».</p>
              </div>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={chiffrerCdc} disabled={chiffrage}
                data-testid="devis-generate-btn">
                {chiffrage ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <Calculator className="w-3.5 h-3.5 mr-1.5" />}
                Chiffrer depuis le CDC
              </Button>
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total TTC</div>
                <div className="font-bold text-indigo-600 tabular-nums">{euros(totaux.ttc)}</div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-400 bg-white">
                    <th className="text-left font-bold px-5 py-2">Désignation</th>
                    <th className="text-right font-bold px-2 py-2 w-20">Qté</th>
                    <th className="text-left font-bold px-2 py-2 w-28">Unité</th>
                    <th className="text-right font-bold px-2 py-2 w-32">P.U. HT</th>
                    <th className="text-right font-bold px-2 py-2 w-32">Total HT</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {(devis.lines || []).map((l, i) => (
                    <tr key={i} className="border-t border-slate-100 align-top">
                      <td className="px-5 py-2">
                        <input value={l.label || ""} onChange={(e) => majLigne(i, "label", e.target.value)}
                          placeholder="Lot 1 — …"
                          className="w-full border border-transparent hover:border-slate-200 focus:border-indigo-300 rounded px-2 py-1 text-sm font-medium text-slate-800 focus:outline-none" />
                        <input value={l.description || ""} onChange={(e) => majLigne(i, "description", e.target.value)}
                          placeholder="Détail de la prestation"
                          className="w-full border border-transparent hover:border-slate-200 focus:border-indigo-300 rounded px-2 py-1 text-xs text-slate-500 focus:outline-none" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.5" value={l.quantity ?? 0} onChange={(e) => majLigne(i, "quantity", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-sm text-right tabular-nums" />
                      </td>
                      <td className="px-2 py-2">
                        <input value={l.unit || ""} onChange={(e) => majLigne(i, "unit", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-sm" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="10" value={l.unit_price ?? 0} onChange={(e) => majLigne(i, "unit_price", e.target.value)}
                          className="w-full border border-slate-200 rounded px-2 py-1 text-sm text-right tabular-nums" />
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                        {euros(totaux.detail[i])}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button onClick={() => supprimerLigne(i)} title="Supprimer la ligne"
                          className="text-slate-300 hover:text-red-600 transition">
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!(devis.lines || []).length && (
                    <tr className="border-t border-slate-100">
                      <td colSpan={6} className="px-5 py-6 text-center text-sm text-slate-400">
                        Aucune ligne — générez le devis avec le cahier des charges ou ajoutez-les à la main.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={ajouterLigne}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter une ligne
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-5 px-5 py-4 border-t border-slate-100 bg-slate-50/40">
              <div className="space-y-3">
                {field("Délai de réalisation",
                  <Input value={devis.delay || ""} onChange={(e) => majDevis("delay", e.target.value)}
                    placeholder="10 semaines à compter de la signature" />)}
                {field("Modalités de règlement",
                  <textarea value={devis.payment_terms || ""} onChange={(e) => majDevis("payment_terms", e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[60px]" />)}
                {field("Précisions",
                  <textarea value={devis.notes || ""} onChange={(e) => majDevis("notes", e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[48px]" />)}
              </div>
              <div>
                <div className="grid grid-cols-3 gap-3">
                  {field("TVA %",
                    <input type="number" step="0.5" value={devis.tva_rate ?? 8.5} onChange={(e) => majDevis("tva_rate", nombre(e.target.value))}
                      className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-right tabular-nums" />)}
                  {field("Remise %",
                    <input type="number" step="1" value={devis.discount_pct ?? 0} onChange={(e) => majDevis("discount_pct", nombre(e.target.value))}
                      className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-right tabular-nums" />)}
                  {field("Validité (j)",
                    <input type="number" step="5" value={devis.validity_days ?? 30} onChange={(e) => majDevis("validity_days", nombre(e.target.value))}
                      className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-right tabular-nums" />)}
                </div>
                <div className="mt-4 rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 text-sm">
                  <div className="flex justify-between px-3.5 py-2">
                    <span className="text-slate-500">Total HT</span>
                    <span className="tabular-nums font-medium text-slate-800">{euros(totaux.total_ht)}</span>
                  </div>
                  {!!totaux.remise && (
                    <div className="flex justify-between px-3.5 py-2">
                      <span className="text-slate-500">Remise {totaux.remise_pct} %</span>
                      <span className="tabular-nums text-slate-800">− {euros(totaux.remise)}</span>
                    </div>
                  )}
                  <div className="flex justify-between px-3.5 py-2">
                    <span className="text-slate-500">TVA {totaux.taux_tva} %</span>
                    <span className="tabular-nums text-slate-800">{euros(totaux.tva)}</span>
                  </div>
                  <div className="flex justify-between px-3.5 py-2.5 bg-indigo-600 rounded-b-lg">
                    <span className="font-bold text-white">Total TTC</span>
                    <span className="tabular-nums font-bold text-white">{euros(totaux.ttc)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Documents enregistrés */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center gap-2.5 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
              <FolderArchive className="w-4 h-4 text-indigo-600" />
              <div>
                <h2 className="font-bold text-slate-900 text-sm">Documents de l'opportunité</h2>
                <p className="text-[11px] text-slate-500">
                  Les PDF enregistrés restent ici : ce sont les versions envoyées au client.
                </p>
              </div>
              <div className="flex-1" />
              <span className="text-xs text-slate-400 tabular-nums">
                {documents.length} document{documents.length > 1 ? "s" : ""}
              </span>
            </div>
            {documents.length === 0 ? (
              <div className="px-5 py-7 text-center text-sm text-slate-400">
                Aucun PDF enregistré — utilisez « Exporter en PDF → Enregistrer dans l'opportunité ».
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {documents.map((d) => (
                  <div key={d.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      d.mode === "cdc_devis" ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-500"
                    }`}>
                      {d.mode === "cdc_devis" ? <Receipt className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{d.title}</div>
                      <div className="text-[11px] text-slate-500">
                        {d.filename} · {(d.created_at || "").slice(0, 10)} à {(d.created_at || "").slice(11, 16)}
                        {d.size ? ` · ${Math.max(1, Math.round(d.size / 1024))} Ko` : ""}
                        {d.reference ? ` · Réf. ${d.reference}` : ""}
                      </div>
                    </div>
                    <div className="flex-1" />
                    <Button variant="outline" size="sm" onClick={() => telechargerDocument(d)}>
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Télécharger
                    </Button>
                    <button onClick={() => supprimerDocument(d)} title="Supprimer"
                      className="text-slate-300 hover:text-red-600 transition p-1">
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Barre d'actions */}
          <div className="sticky bottom-4 flex flex-wrap items-center gap-2.5 bg-white/95 backdrop-blur border border-slate-200 rounded-xl shadow-sm px-4 py-3">
            <Button onClick={() => enregistrerCdc()} disabled={savingCdc}
              className="bg-indigo-600 hover:bg-indigo-700" data-testid="cdc-save-btn">
              {savingCdc ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Enregistrer
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={!!exportMode} data-testid="cdc-export-btn">
                  {exportMode ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
                  Exporter en PDF
                  <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                  Télécharger
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => exporterCdc("cdc")} className="flex-col items-start gap-0.5 py-2.5">
                  <span className="font-semibold text-slate-800 flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-indigo-600" /> Cahier des charges
                  </span>
                  <span className="text-xs text-slate-500">Le document seul, en-tête NovaForge.</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exporterCdc("cdc_devis")} className="flex-col items-start gap-0.5 py-2.5">
                  <span className="font-semibold text-slate-800 flex items-center gap-2">
                    <Receipt className="w-3.5 h-3.5 text-indigo-600" /> CDC + devis structuré
                  </span>
                  <span className="text-xs text-slate-500">
                    Ajoute la proposition commerciale chiffrée ({euros(totaux.ttc)} TTC).
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                  Enregistrer dans l'opportunité
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => enregistrerPdf("cdc")} className="flex-col items-start gap-0.5 py-2.5">
                  <span className="font-semibold text-slate-800 flex items-center gap-2">
                    <FolderArchive className="w-3.5 h-3.5 text-indigo-600" /> Cahier des charges
                  </span>
                  <span className="text-xs text-slate-500">Conservé dans les documents ci-dessus.</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => enregistrerPdf("cdc_devis")} className="flex-col items-start gap-0.5 py-2.5">
                  <span className="font-semibold text-slate-800 flex items-center gap-2">
                    <FolderArchive className="w-3.5 h-3.5 text-indigo-600" /> CDC + devis structuré
                  </span>
                  <span className="text-xs text-slate-500">Fige la version envoyée au client.</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <span className="text-xs text-slate-400">
              {modifie ? "Modifications non enregistrées" : "À jour"}
            </span>
          </div>
        </div>
        )}
      </div>
    </CRMLayout>
  );
}
